import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  ColumnLayout,
  Badge,
  StatusIndicator,
  Table,
  Link,
  ProgressBar,
  Alert,
  Tabs,
  Grid,
  Button,
  Modal,
  Spinner
} from '@cloudscape-design/components';
import { s3Service } from '../services/s3Service';
import ModernizationCharts from './ModernizationCharts';
import { isESCCalculatorUrl, getESCCurrency } from '../utils/escUtils';

const PricingCalculatorTab = ({ request, preloadedData, isESC = false }) => {
  const [calculatorDetails, setCalculatorDetails] = useState(preloadedData || null);
  const [loading, setLoading] = useState(!preloadedData);
  const [error, setError] = useState(null);
  const [activeMainTab, setActiveMainTab] = useState('general');
  const [activeSubTab, setActiveSubTab] = useState('annual-run-rate');

  const validationData = request.validation_data?.detailed_validations || {};
  const pricingData = validationData.pricing_calculator || {};
  const fundRequestId = request.apfp_fundrequestid || request.id;

  // Detect ESC from calculator URL in request data if not explicitly passed
  const calculatorLinks = request.validation_data?.complete_api_data?.fundRequest?.artifacts?.pricingCalculatorLinks || [];
  const detectedESC = isESC || calculatorLinks.some(url => isESCCalculatorUrl(url)) || calculatorDetails?.is_esc === true;
  const currency = getESCCurrency(detectedESC);

  useEffect(() => {
    if (preloadedData) return; // skip fetch — data already provided by caller
    loadCalculatorDetails();
  }, [fundRequestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCalculatorDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const details = await s3Service.getCalculatorDetails(fundRequestId);
      setCalculatorDetails(details);
    } catch (error) {
      console.error('Error loading calculator details:', error);
      setError(error.message || 'Failed to load pricing calculator data');
      setCalculatorDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getServicePathway = (serviceCode, modernizationPathways) => {
    // Get pathway from backend data
    if (!modernizationPathways?.pathways) return 'Non Modern';
    
    for (const pathway of modernizationPathways.pathways) {
      const service = pathway.services?.find(s => s.serviceCode === serviceCode);
      if (service) {
        return pathway.name;
      }
    }
    return 'Non Modern';
  };

  const getEligibilityBadge = (isEligible) => {
    return (
      <Badge color={isEligible ? 'blue' : 'red'}>
        {isEligible ? 'MAP ELIGIBLE' : 'NOT ELIGIBLE'}
      </Badge>
    );
  };

  /**
   * Aggregate services by service_code (case-insensitive grouping)
   * Groups duplicate service line items and sums their costs
   * @param {Array} services - Array of service objects
   * @returns {Array} Aggregated services with summed costs and counts
   */
  const aggregateServicesByCode = (services) => {
    if (!services || services.length === 0) {
      return [];
    }

    // Group by service_code (case-insensitive)
    const grouped = services.reduce((acc, service) => {
      const serviceCode = service.service_code || '';
      const key = serviceCode.toLowerCase(); // Case-insensitive grouping
      
      if (!acc[key]) {
        acc[key] = {
          service_code: serviceCode, // Keep original casing from first occurrence
          service_name: service.service_name || '',
          monthly_cost: 0,
          map_qualified_mrr: 0,
          count: 0,
          modernization_pathway: service.modernization_pathway || 'Unknown',  // Use provided pathway
          region: service.region // Keep first region
        };
      }
      
      // Sum costs and increment count
      acc[key].monthly_cost += service.monthly_cost || 0;
      acc[key].map_qualified_mrr += service.map_qualified_mrr || 0;
      acc[key].count += 1;
      
      return acc;
    }, {});
    
    // Convert to array and calculate derived fields
    const aggregatedArray = Object.values(grouped);
    const totalMonthlyCost = aggregatedArray.reduce((sum, s) => sum + s.monthly_cost, 0);
    
    return aggregatedArray.map(service => ({
      ...service,
      annual_cost: service.monthly_cost * 12,
      percentage: totalMonthlyCost > 0 
        ? (service.monthly_cost / totalMonthlyCost) * 100 
        : 0,
      map_eligible: service.map_qualified_mrr > 0
    }));
  };

  // Calculate summary data
  const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
  const services = calculatorDetails?.services || [];
  
  const totalARR = pricingData.annual_recurring_revenue || 
                   parseFloat((fundRequest.arrUsdAmount || '0').replace(/[^0-9.-]+/g, '')) ||
                   calculatorDetails?.modernizationPathways?.totalARR || 0;
  
  // Calculate Annual ARR (total before exclusions) and Total Excluded
  const annualARR = services.reduce((sum, service) => sum + ((service.monthly_cost * 12) + (service.upfront_cost || 0)), 0);
  const totalExcludedARR = services.reduce((sum, service) => sum + (service.monthly_always_excluded * 12 || 0), 0);
  
  // Calculate qualified ARR as Annual ARR - Total Excluded
  const qualifiedARR = annualARR - totalExcludedARR;
  
  const optimizationPercentage = pricingData.not_optimized_percentage || 0;
  const optimizationThresholdMet = pricingData.optimization_threshold_met;

  // Enhanced 4-column dashboard summary
  const renderEnhancedSummary = () => {
    const totalServices = services.length;
    
    // Helper function to parse exclusion breakdown
    const parseExclusionBreakdown = (breakdownString) => {
      if (!breakdownString) return null;
      try {
        // Convert Python dict format to JSON
        const jsonString = breakdownString
          .replace(/'/g, '"')
          .replace(/None/g, 'null')
          .replace(/True/g, 'true')
          .replace(/False/g, 'false');
        return JSON.parse(jsonString);
      } catch (e) {
        console.error('Error parsing exclusion_breakdown:', e);
        return null;
      }
    };
    
    // Use backend modernization data
    const modernizationData = calculatorDetails?.modernizationPathways || {};
    const modernServicesCount = modernizationData.pathways
      ?.filter(p => p.name !== 'Non Modern')
      .reduce((sum, p) => sum + p.serviceCount, 0) || 0;
    const modernServicesARR = modernizationData.modernARR || 0;
    const totalAnnualCost = modernizationData.totalARR || 0;
    
    // Calculate Annual ARR before exclusions (for Qualification ARR display only)
    const annualARRBeforeExclusions = services.reduce((sum, service) => sum + ((service.monthly_cost * 12) + (service.upfront_cost || 0)), 0);
    
    const eligibleServices = services.filter(service => service.map_qualified_mrr > 0).length;
    const eligibleServicesARR = services
      .filter(service => service.map_qualified_mrr > 0)
      .reduce((sum, service) => sum + (service.monthly_cost * 12 || 0), 0);
    const totalMonthlyCost = services.reduce((sum, service) => sum + (service.monthly_cost || 0), 0);
    
    // Calculate modern services percentage based on ARR, not count
    const modernServicesPercentage = totalAnnualCost > 0 ? ((modernServicesARR / totalAnnualCost) * 100).toFixed(1) : 0;
    // Calculate eligible services percentage based on ARR, not count
    const eligibleServicesPercentage = totalAnnualCost > 0 ? ((eligibleServicesARR / totalAnnualCost) * 100).toFixed(1) : 0;

    // Calculate excluded services data
    const excludedServices = services.filter(s => s.monthly_always_excluded > 0);
    const totalExcluded = excludedServices.reduce((sum, s) => {
      const breakdown = parseExclusionBreakdown(s.exclusion_breakdown);
      // Use arr from breakdown if available (handles upfront costs correctly)
      const annualAmount = breakdown?.arr !== undefined 
        ? breakdown.arr 
        : s.monthly_always_excluded * 12;
      return sum + annualAmount;
    }, 0);
    const excludedPercentage = annualARRBeforeExclusions > 0 
      ? (totalExcluded / annualARRBeforeExclusions) * 100 
      : 0;
    
    // Group by exclusion type
    const excludedByType = {};
    excludedServices.forEach(service => {
      const breakdown = parseExclusionBreakdown(service.exclusion_breakdown);
      if (!breakdown) return;
      
      const type = breakdown.type || 'other';
      if (!excludedByType[type]) {
        excludedByType[type] = {
          totalAnnual: 0,
          services: []
        };
      }
      
      // Use the arr field from breakdown if available (handles upfront costs correctly)
      // Otherwise fall back to monthly_always_excluded * 12
      const annualAmount = breakdown.arr !== undefined 
        ? breakdown.arr 
        : service.monthly_always_excluded * 12;
      
      excludedByType[type].totalAnnual += annualAmount;
      excludedByType[type].services.push({
        name: service.service_name,
        breakdown: breakdown
      });
    });

    return (
      <Container header={<Header variant="h2">Dashboard Overview</Header>}>
        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
          {/* MAP ARR Qualification */}
          <Container>
            <Box variant="h3" color="text-status-info">MAP ARR Qualification</Box>
            <SpaceBetween size="s">
              {/* Calculator Provided ARR */}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>Calculator Provided ARR</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold">
                  {formatCurrency(annualARRBeforeExclusions)}
                </Box>
              </div>
              
              {/* MAP Excluded Services */}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>MAP Excluded Services</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  ({formatCurrency(totalExcluded)})
                </Box>
                <Box variant="small" color="text-status-inactive">
                  Data Transfer, Support, Glacier
                </Box>
              </div>
              
              {/* Non-Optimized ARR */}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>Non-Optimized ARR</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  ({formatCurrency(services.reduce((sum, s) => sum + (s.ec2_sp_annual_savings || 0), 0))})
                </Box>
                <Box variant="small" color="text-status-inactive">
                  EC2 Savings Plans Opportunity
                </Box>
              </div>
              
              {/* Total Deductions */}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>Total Deductions</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  ({formatCurrency(totalExcluded + services.reduce((sum, s) => sum + (s.ec2_sp_annual_savings || 0), 0))})
                </Box>
              </div>
              
              {/* Final MAP Qualified ARR */}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>Final MAP Qualified ARR</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-success">
                  {formatCurrency(qualifiedARR - services.reduce((sum, s) => sum + (s.ec2_sp_annual_savings || 0), 0))}
                </Box>
              </div>
              
              {/* ARR Variance Message from Agent Validation */}
              {(() => {
                const approvalSummary = request.approval_summary || {};
                const keyFindings = approvalSummary.key_findings || [];
                
                const arrVarianceMessage = keyFindings.find(finding => 
                  finding.toLowerCase().includes('calculator arr variance') || 
                  finding.toLowerCase().includes('arr variance')
                );
                
                if (arrVarianceMessage) {
                  const isSuccess = arrVarianceMessage.includes('✅') || 
                                   arrVarianceMessage.toLowerCase().includes('within') ||
                                   !arrVarianceMessage.toLowerCase().includes('exceed');
                  
                  return (
                    <Alert
                      type={isSuccess ? 'success' : 'error'}
                      statusIconAriaLabel={isSuccess ? 'Success' : 'Error'}
                    >
                      {arrVarianceMessage}
                    </Alert>
                  );
                }
                return null;
              })()}
            </SpaceBetween>
          </Container>
          
          {/* MAP Always Excluded Services */}
          <Container>
            <Box variant="h3" color="text-status-info">MAP Always Excluded Services</Box>
            <SpaceBetween size="s">
              {/* Critical finding alert based on threshold (€15k for ESC, $15k for standard) */}
              {totalExcluded > 15000 ? (
                <Alert type="error" statusIconAriaLabel="Error">
                  Critical finding: non-eligible services exceed {formatCurrency(15000)} ARR threshold — recommend rejection
                </Alert>
              ) : totalExcluded > 0 ? (
                <Alert type="info" statusIconAriaLabel="Info">
                  While approved, non-eligible services should not be included in qualified ARR for future reference
                </Alert>
              ) : null}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>Total Excluded</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  {formatCurrency(totalExcluded)}
                </Box>
                <Box variant="small" color="text-status-inactive">
                  ({excludedPercentage.toFixed(1)}% of ARR)
                </Box>
              </div>
              
              {Object.keys(excludedByType).length > 0 && (
                <div>
                  <Box variant="awsui-key-label">
                    <span style={{ textDecoration: 'underline', fontStyle: 'italic' }}>Breakdown</span>
                  </Box>
                  <SpaceBetween size="xs">
                    {Object.entries(excludedByType).map(([type, data]) => (
                      <Box key={type}>
                        <Box variant="strong">
                          {type === 'aws_support' ? 'AWS Support' : 
                           type === 'data_transfer' ? 'AWS Data Transfer Out' : 
                           type === 'glacier_deep_archive' ? 'Amazon S3 Glacier Deep Archive' : 
                           type === 'cloudfront_data_transfer' ? 'CloudFront Data Transfer Out' : type}
                        </Box>
                        <Box color="text-status-error">
                          {formatCurrency(data.totalAnnual)}
                        </Box>
                        {data.services[0]?.breakdown?.reason && (
                          <Box variant="small" color="text-status-inactive">
                            {data.services[0].breakdown.reason}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </SpaceBetween>
                </div>
              )}
            </SpaceBetween>
          </Container>
          
          {/* Optimization Opportunities */}
          <Container>
            <Box variant="h3" color="text-status-info">Optimization Opportunities</Box>
            <SpaceBetween size="s">
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ textDecoration: 'underline', fontStyle: 'italic' }}>Potential ARR Savings</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  {(() => {
                    const services = calculatorDetails?.services || [];
                    const totalSavings = services
                      .filter(s => s.ec2_sp_annual_savings)
                      .reduce((sum, s) => sum + s.ec2_sp_annual_savings, 0);
                    return formatCurrency(totalSavings);
                  })()}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ textDecoration: 'underline', fontStyle: 'italic' }}>Non-Optimized %</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  {(() => {
                    // Use stored percentage from agent validation when available (FR-linked mode).
                    // Fall back to recalculating from services using total calculator ARR
                    // (before exclusions) as denominator — matches Calcbuster methodology.
                    if (optimizationPercentage != null) {
                      return `${Number(optimizationPercentage).toFixed(1)}%`;
                    }
                    const services = calculatorDetails?.services || [];
                    const totalSavings = services
                      .filter(s => s.ec2_sp_annual_savings)
                      .reduce((sum, s) => sum + s.ec2_sp_annual_savings, 0);
                    const totalCalcARR = services.reduce((sum, s) => sum + ((s.monthly_cost * 12) + (s.upfront_cost || 0)), 0);
                    const percentage = totalCalcARR > 0 ? (totalSavings / totalCalcARR) * 100 : 0;
                    return `${percentage.toFixed(1)}%`;
                  })()}
                </Box>
                <Box variant="small" color="text-status-inactive">
                  of Total ARR
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ textDecoration: 'underline', fontStyle: 'italic' }}>EC2 Savings Plans</span>
                </Box>
                <Box color="text-status-info">
                  {(() => {
                    const services = calculatorDetails?.services || [];
                    const count = services.filter(s => s.ec2_sp_annual_savings > 0).length;
                    return `${count} service${count !== 1 ? 's' : ''} optimizable`;
                  })()}
                </Box>
              </div>
              {/* Download Optimization Report */}
              {(() => {
                const optimizableServices = (calculatorDetails?.services || [])
                  .filter(s => s.ec2_sp_annual_savings > 0);

                if (optimizableServices.length === 0) {
                  return (
                    <Box color="text-status-inactive" fontSize="body-s">
                      No EC2 Savings Plan opportunities found
                    </Box>
                  );
                }

                const downloadOptimizationReport = () => {
                  const parseConfigSummary = (config, field) => {
                    if (!config) return '-';
                    const match = config.match(new RegExp(`${field}\\s*\\(([^)]+)\\)`));
                    return match ? match[1] : '-';
                  };

                  const currencySymbol = detectedESC ? '€' : '$';
                  const headers = [
                    'Service Name', 'Description', 'Region',
                    'Instance Type', 'Operating System',
                    `Monthly Cost (${currencySymbol})`, `Annual SP Savings (${currencySymbol})`,
                    `SP Hourly Rate (${currencySymbol})`, 'Plan Type'
                  ];

                  const rows = optimizableServices.map(s => [
                    s.service_name?.trim() || '-',
                    s.description || '-',
                    s.region || '-',
                    parseConfigSummary(s.config_summary, 'Advance EC2 instance'),
                    parseConfigSummary(s.config_summary, 'Operating system'),
                    s.monthly_cost?.toFixed(2) || '0.00',
                    s.ec2_sp_annual_savings?.toFixed(2) || '0.00',
                    s.ec2_sp_hourly_rate?.toFixed(5) || '0.00000',
                    s.ec2_sp_plan_type || '-'
                  ]);

                  const totalSavings = optimizableServices
                    .reduce((sum, s) => sum + (s.ec2_sp_annual_savings || 0), 0);
                  rows.push(['TOTAL', '', '', '', '', '', totalSavings.toFixed(2), '', '']);

                  const csvContent = [headers, ...rows]
                    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                    .join('\n');

                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `optimization-report-${fundRequestId}.csv`;
                  link.click();
                  URL.revokeObjectURL(url);
                };

                return (
                  <Button iconName="download" variant="normal" onClick={downloadOptimizationReport}>
                    Download Optimization Report
                  </Button>
                );
              })()}
            </SpaceBetween>
          </Container>
        </Grid>
      </Container>
    );
  };

  const renderSummarySection = () => (
    <Container>
      <ColumnLayout columns={3} variant="text-grid">
        {/* General */}
        <div>
          <Box variant="h3">General</Box>
          <SpaceBetween size="s">
            <div>
              <Box variant="awsui-key-label">Annual Run Rate</Box>
              <Box fontSize="heading-l" fontWeight="bold">
                {formatCurrency(totalARR)}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Services estimate processed</Box>
              <Box color="text-status-success">
                ✓ {calculatorDetails?.serviceCount || 0}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Modernization Index</Box>
              <Box>{((qualifiedARR / totalARR) * 100 || 0).toFixed(1)}%</Box>
            </div>
          </SpaceBetween>
        </div>

        {/* Optimizations */}
        <div>
          <Box variant="h3">Optimizations</Box>
          <SpaceBetween size="s">
            <div>
              <Box variant="awsui-key-label">Recommendations</Box>
              <Box color={optimizationThresholdMet ? 'text-status-success' : 'text-status-error'}>
                {optimizationThresholdMet ? '✓ 0' : '⚠ 1'}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Recommendations may lower ARR by</Box>
              <Box>{formatCurrency(0)}</Box>
            </div>
          </SpaceBetween>
        </div>

        {/* Funding Insights */}
        <div>
          <Box variant="h3">Funding Insights</Box>
          <SpaceBetween size="s">
            <div>
              <Box variant="awsui-key-label">Qualification ARR</Box>
              <Box fontSize="heading-m" fontWeight="bold" color="text-status-success">
                {formatCurrency(qualifiedARR)}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Migration Acceleration Program</Box>
              <Box>{(() => {
                // Determine MAP program based on ARR amount
                // ESC uses EUR thresholds: MAP Lite €100K-€2M, MAP Large €2M+
                // Standard AWS uses USD thresholds
                const services = calculatorDetails?.services || [];
                const totalAnnualCost = services.reduce((sum, service) => sum + (service.monthly_cost * 12 || 0), 0);
                const largeThreshold = detectedESC ? 2000000 : 500000;
                return totalAnnualCost > largeThreshold ? 'MAP LARGE' : 'MAP LITE';
              })()}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Proof of Concept</Box>
              <Box>{formatCurrency(0)}</Box>
            </div>
          </SpaceBetween>
        </div>
      </ColumnLayout>
    </Container>
  );

  const renderAnnualRunRate = () => {
    // Use backend pathway data for pie chart
    const modernizationData = calculatorDetails?.modernizationPathways || {};
    const modernServicesARR = modernizationData.modernARR || 0;
    const eligibleServicesARR = services
      .filter(service => service.map_qualified_mrr > 0)
      .reduce((sum, service) => sum + (service.monthly_cost * 12 || 0), 0);
    const pathwayGroups = {};
    let totalCost = 0;

    if (modernizationData.pathways) {
      modernizationData.pathways.forEach(pathway => {
        pathwayGroups[pathway.name] = pathway.arr;
        totalCost += pathway.arr;
      });
    }

    return (
      <SpaceBetween size="l">
        <Container>
          <ColumnLayout columns={2}>
            {/* Bar Chart Area */}
            <div>
              <Box variant="h3">Annual Run Rate</Box>
              <Box variant="small">{currency}</Box>
              
              {/* Simple bar representation */}
              <div style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'end', height: '200px', gap: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div 
                      style={{ 
                        width: '60px', 
                        height: `${Math.min((qualifiedARR / totalARR) * 180 || 0, 180)}px`, 
                        backgroundColor: '#1f77b4',
                        marginBottom: '5px'
                      }}
                    />
                    <Box variant="small" fontWeight="bold">{formatCurrency(qualifiedARR)}</Box>
                    <Box variant="small">Qualification ARR</Box>
                  </div>
                  {eligibleServicesARR > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div 
                      style={{ 
                        width: '60px', 
                        height: `${Math.min((eligibleServicesARR / totalARR) * 180 || 0, 180)}px`, 
                        backgroundColor: '#ff7f0e',
                        marginBottom: '5px'
                      }}
                    />
                    <Box variant="small" fontWeight="bold">{formatCurrency(eligibleServicesARR)}</Box>
                    <Box variant="small">MAP Eligible ARR</Box>
                  </div>
                  )}
                  {modernServicesARR > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div 
                      style={{ 
                        width: '60px', 
                        height: `${Math.min((modernServicesARR / totalARR) * 180 || 0, 180)}px`, 
                        backgroundColor: '#2ca02c',
                        marginBottom: '5px'
                      }}
                    />
                    <Box variant="small" fontWeight="bold">{formatCurrency(modernServicesARR)}</Box>
                    <Box variant="small">Modernization ARR</Box>
                  </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div 
                      style={{ 
                        width: '60px', 
                        height: '180px', 
                        backgroundColor: '#d62728',
                        marginBottom: '5px'
                      }}
                    />
                    <Box variant="small" fontWeight="bold">{formatCurrency(totalARR)}</Box>
                    <Box variant="small">Total Annual Run Rate</Box>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div style={{ marginTop: '20px' }}>
                <SpaceBetween size="xs">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#1f77b4' }} />
                    <Box variant="small">Qualification ARR</Box>
                  </div>
                  {eligibleServicesARR > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#ff7f0e' }} />
                    <Box variant="small">MAP Eligible ARR</Box>
                  </div>
                  )}
                  {modernServicesARR > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#2ca02c' }} />
                    <Box variant="small">Modernization ARR</Box>
                  </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#d62728' }} />
                    <Box variant="small">Total Annual Run Rate</Box>
                  </div>
                </SpaceBetween>
              </div>
            </div>

            {/* Pie Chart Area */}
            <div>
              <Box variant="h3">Modernization Index</Box>
              
              {/* Simple pie chart representation */}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <div 
                  style={{ 
                    width: '150px', 
                    height: '150px', 
                    borderRadius: '50%',
                    backgroundColor: Object.keys(pathwayGroups).length > 1 ? 
                      `conic-gradient(${Object.entries(pathwayGroups).map(([pathway, cost], index) => {
                        const colors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b'];
                        const percentage = (cost / totalCost) * 100;
                        return `${colors[index % colors.length]} ${index === 0 ? 0 : Object.entries(pathwayGroups).slice(0, index).reduce((sum, [, c]) => sum + (c / totalCost) * 100, 0)}% ${Object.entries(pathwayGroups).slice(0, index + 1).reduce((sum, [, c]) => sum + (c / totalCost) * 100, 0)}%`;
                      }).join(', ')})` : '#1f77b4'
                  }}
                />
              </div>

              <Box variant="small" textAlign="center" padding={{ top: 's' }}>
                {formatCurrency(totalCost)} • 100%
              </Box>
            </div>
          </ColumnLayout>
        </Container>

        {/* ARR Breakdown List */}
        <Container>
          <SpaceBetween size="s">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#1f77b4' }} />
              <Box variant="strong">Qualification ARR:</Box>
              <Box>Heavy State Annual Run Rate (optimized) used for funding</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#ff7f0e' }} />
              <Box variant="strong">MAP Eligible ARR:</Box>
              <Box>Annual Run Rate generated by the eligible services</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#2ca02c' }} />
              <Box variant="strong">Modernization ARR:</Box>
              <Box>Annual Run Rate generated by modern services</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#9467bd' }} />
              <Box variant="strong">SAP and Oracle Eligible ARR:</Box>
              <Box>Annual Run Rate generated by the SAP and Oracle eligible services</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#8c564b' }} />
              <Box variant="strong">DB and Analytics Eligible ARR:</Box>
              <Box>Annual Run Rate generated by the DB and Analytics eligible services</Box>
            </div>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    );
  };

  // Enhanced Services Table with Modal Details
  const [selectedService, setSelectedService] = useState(null);
  const [showServiceModal, setShowServiceModal] = useState(false);

  const renderServiceModal = () => (
    <Modal
      onDismiss={() => setShowServiceModal(false)}
      visible={showServiceModal}
      size="large"
      header={selectedService?.service_name || 'Service Details'}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setShowServiceModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setShowServiceModal(false)}>
              Close
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {selectedService && (
        <SpaceBetween size="l">
          <Container header={<Header variant="h3">Service Information</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <SpaceBetween size="s">
                <div>
                  <Box variant="awsui-key-label">Service Code</Box>
                  <Box>{selectedService.service_code}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Service Name</Box>
                  <Box>{selectedService.service_name}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Region</Box>
                  <Box>{selectedService.region || 'us-east-1'}</Box>
                </div>
              </SpaceBetween>
              <SpaceBetween size="s">
                <div>
                  <Box variant="awsui-key-label">Monthly Cost</Box>
                  <Box fontSize="heading-m" fontWeight="bold">
                    {formatCurrency(selectedService.monthly_cost)}
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Annual Cost</Box>
                  <Box>{formatCurrency(selectedService.monthly_cost * 12)}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">MAP Qualified MRR</Box>
                  <Box>{formatCurrency(selectedService.map_qualified_mrr)}</Box>
                </div>
              </SpaceBetween>
            </ColumnLayout>
          </Container>
          
          <Container header={<Header variant="h3">Modernization Analysis</Header>}>
            <SpaceBetween size="s">
              <div>
                <Box variant="awsui-key-label">Modernization Pathway</Box>
                <Badge color={selectedService.modernization_pathway !== 'Non Modern' ? 'blue' : 'red'}>
                  {selectedService.modernization_pathway}
                </Badge>
              </div>
              <div>
                <Box variant="awsui-key-label">MAP Eligibility</Box>
                {getEligibilityBadge(selectedService.map_qualified_mrr > 0)}
              </div>
              <div>
                <Box variant="awsui-key-label">Optimization Potential</Box>
                <StatusIndicator type={selectedService.map_qualified_mrr > 0 ? 'success' : 'warning'}>
                  {selectedService.map_qualified_mrr > 0 ? 'Optimized' : 'Review Recommended'}
                </StatusIndicator>
              </div>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      )}
    </Modal>
  );

  const renderEnhancedServicesTable = () => {
    if (!calculatorDetails?.services) {
      return (
        <Container>
          <Box textAlign="center" color="inherit">
            <b>No service details available</b>
          </Box>
        </Container>
      );
    }

    // Enrich services with pathway from backend
    const modernizationData = calculatorDetails?.modernizationPathways || {};
    const servicesWithPathway = calculatorDetails.services.map(service => ({
      ...service,
      modernization_pathway: getServicePathway(service.service_code, modernizationData)
    }));

    const aggregatedServices = aggregateServicesByCode(servicesWithPathway);

    return (
      <Container>
        <Table
          columnDefinitions={[
            {
              id: 'service_name',
              header: 'Service',
              cell: item => item.service_name,
              sortingField: 'service_name'
            },
            {
              id: 'service_code',
              header: 'Service Code',
              cell: item => item.service_code,
              sortingField: 'service_code'
            },
            {
              id: 'modernization_pathway',
              header: 'Modernization',
              cell: item => (
                <Badge color={item.modernization_pathway !== 'Non Modern' ? 'blue' : 'red'}>
                  {item.modernization_pathway}
                </Badge>
              )
            },
            {
              id: 'eligibility',
              header: 'MAP Eligibility',
              cell: item => getEligibilityBadge(item.map_eligible)
            },
            {
              id: 'monthly_cost',
              header: 'Monthly Cost',
              cell: item => formatCurrency(item.monthly_cost),
              sortingField: 'monthly_cost'
            },
            {
              id: 'annual_cost',
              header: 'Annual Cost',
              cell: item => formatCurrency(item.annual_cost),
              sortingField: 'annual_cost'
            },
            {
              id: 'count',
              header: 'Line Items',
              cell: item => item.count,
              sortingField: 'count'
            }
          ]}
          items={aggregatedServices}
          loadingText="Loading services"
          sortingDisabled={false}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No services found</b>
            </Box>
          }
          header={
            <Header
              counter={`(${aggregatedServices.length})`}
              description="Services aggregated by service code for easier reading"
            >
              Service Estimates
            </Header>
          }
          pagination
        />
        {renderServiceModal()}
      </Container>
    );
  };

  const renderEligibleServices = () => {
    const services = calculatorDetails?.services || [];
    
    // Filter to only eligible services (map_qualified_mrr > 0)
    const eligibleServicesRaw = services.filter(service => 
      (service.map_qualified_mrr || 0) > 0
    );
    
    // Aggregate eligible services by service_code
    const eligibleServices = aggregateServicesByCode(eligibleServicesRaw);
    
    return (
      <Container>
        <Table
          columnDefinitions={[
            {
              id: 'service_code',
              header: 'Service Code',
              cell: item => item.service_code,
              sortingField: 'service_code'
            },
            {
              id: 'service_name',
              header: 'Service Name',
              cell: item => item.service_name,
              sortingField: 'service_name'
            },
            {
              id: 'annual_cost',
              header: 'Annual Cost',
              cell: item => formatCurrency(item.annual_cost),
              sortingField: 'annual_cost'
            },
            {
              id: 'monthly_cost',
              header: 'Monthly Cost',
              cell: item => formatCurrency(item.monthly_cost),
              sortingField: 'monthly_cost'
            },
            {
              id: 'count',
              header: 'Line Items',
              cell: item => item.count,
              sortingField: 'count'
            },
            {
              id: 'map_qualified_mrr',
              header: 'MAP Qualified MRR',
              cell: item => formatCurrency(item.map_qualified_mrr),
              sortingField: 'map_qualified_mrr'
            }
          ]}
          items={eligibleServices}
          loadingText="Loading eligible services"
          sortingDisabled={false}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No MAP eligible services found</b>
              <Box variant="p" color="inherit">
                Services must have MAP qualified MRR greater than zero to be eligible.
              </Box>
            </Box>
          }
          header={
            <Header
              counter={`(${eligibleServices.length})`}
              description="Services with MAP qualified MRR > 0, aggregated by service code"
            >
              MAP Eligible Services
            </Header>
          }
          pagination
        />
      </Container>
    );
  };

  // Recommendations System
  const generateRecommendations = () => {
    // Return empty array - no mock recommendations
    return [];
  };

  const renderRecommendationsTab = () => {
    const recommendations = generateRecommendations();

    if (recommendations.length === 0) {
      return (
        <Container>
          <Box textAlign="center" color="inherit" padding="xxl">
            <StatusIndicator type="success">
              <Box variant="h3">No Optimization Recommendations</Box>
              <Box variant="p" color="inherit">
                Your current configuration appears to be well optimized. All services meet the optimization thresholds.
              </Box>
            </StatusIndicator>
          </Box>
        </Container>
      );
    }

    return (
      <Container>
        <Table
          columnDefinitions={[
            {
              id: 'id',
              header: 'ID',
              cell: item => item.id,
              sortingField: 'id'
            },
            {
              id: 'severity',
              header: 'Severity',
              cell: item => (
                <Badge color={
                  item.severity === 'High' ? 'red' : 
                  item.severity === 'Medium' ? 'yellow' : 'blue'
                }>
                  {item.severity}
                </Badge>
              ),
              sortingField: 'priority'
            },
            {
              id: 'service',
              header: 'Service',
              cell: item => item.service,
              sortingField: 'service'
            },
            {
              id: 'recommendation',
              header: 'Recommendation',
              cell: item => (
                <Box>
                  <Box variant="strong">{item.title}</Box>
                  <Box variant="small" color="text-body-secondary">
                    {item.description}
                  </Box>
                </Box>
              )
            },
            {
              id: 'savings',
              header: 'Potential Annual Savings',
              cell: item => (
                <Box color="text-status-success" fontWeight="bold">
                  {formatCurrency(item.potentialSavings)}
                </Box>
              ),
              sortingField: 'potentialSavings'
            },
            {
              id: 'tags',
              header: 'Tags',
              cell: item => (
                <SpaceBetween direction="horizontal" size="xs">
                  {item.tags.map((tag, index) => (
                    <Badge key={index} color="blue">{tag}</Badge>
                  ))}
                </SpaceBetween>
              )
            }
          ]}
          items={recommendations}
          loadingText="Loading recommendations"
          sortingDisabled={false}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No recommendations found</b>
            </Box>
          }
          header={
            <Header
              counter={`(${recommendations.length})`}
              description="Optimization recommendations to reduce costs and improve performance"
            >
              Optimization Recommendations
            </Header>
          }
          footer={
            recommendations.length > 0 && (
              <Box textAlign="center" padding="s">
                <Box variant="strong">
                  Total Potential Annual Savings: {formatCurrency(recommendations.reduce((sum, rec) => sum + rec.potentialSavings, 0))}
                </Box>
              </Box>
            )
          }
        />
      </Container>
    );
  };

  const renderFundElig = () => {
    // Use validation from calculator processing (works for both standalone and FR-linked flows)
    const calcValidation = calculatorDetails?.validation || {};
    const calcOptimizationMet = calcValidation.optimization_metrics
      ? calcValidation.optimization_metrics.not_optimized_percentage <= 10.0
      : optimizationThresholdMet;

    return (
      <Container>
        <Alert type="info" header="Funding Eligibility Information">
          <SpaceBetween size="s">
            <Box>
              <Box variant="strong">MAP Qualified ARR:</Box> {formatCurrency(qualifiedARR)}
            </Box>
            <Box>
              <Box variant="strong">Optimization Status:</Box> {calcOptimizationMet ? 'Meets threshold' : 'Below threshold'}
            </Box>
          </SpaceBetween>
        </Alert>
      </Container>
    );
  };

  // Modernization Tab Content
  const renderModernizationTab = () => {
    const modernizationData = calculatorDetails?.modernizationPathways || {};
    return (
      <ModernizationCharts
        modernizationData={modernizationData}
        formatCurrency={formatCurrency}
      />
    );
  };

  const renderMainTabContent = () => {
    const subTabs = [
      {
        id: 'annual-run-rate',
        label: 'Annual Run Rate',
        content: renderAnnualRunRate()
      },
      {
        id: 'eligible-services',
        label: 'Eligible Services',
        content: renderEligibleServices()
      },
      {
        id: 'fund-elig',
        label: 'Funding Eligibility',
        content: renderFundElig()
      }
    ];

    return (
      <Tabs
        tabs={subTabs}
        activeTabId={activeSubTab}
        onChange={({ detail }) => setActiveSubTab(detail.activeTabId)}
      />
    );
  };

  // Calculate if we should show optional tabs
  const modernizationData = calculatorDetails?.modernizationPathways || {};
  const modernServices = modernizationData.pathways
    ?.filter(p => p.name !== 'Non Modern')
    .flatMap(p => p.services) || [];
  const recommendations = generateRecommendations();

  const mainTabs = [
    {
      id: 'general',
      label: 'General',
      content: renderEnhancedServicesTable()
    },
    ...(modernServices.length > 0 || services.length > 0 ? [{
      id: 'modernization',
      label: 'Modernization',
      content: renderModernizationTab()
    }] : []),
    ...(recommendations.length > 0 ? [{
      id: 'recommendations',
      label: 'Recommendations',
      content: renderRecommendationsTab()
    }] : []),
    {
      id: 'migration-acceleration',
      label: 'Migration Acceleration Program',
      content: renderMainTabContent()
    }
  ];

  if (loading) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          Loading pricing calculator details...
        </Box>
      </Container>
    );
  }

  // Check if pricing calculator data is not available
  if (error || !calculatorDetails || !calculatorDetails.services || calculatorDetails.services.length === 0) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          <StatusIndicator type="info">
            <Box variant="h3">Pricing Calculator Not Available</Box>
            <Box variant="p" color="inherit" padding={{ top: 's' }}>
              This funding request does not have pricing calculator data available. 
              The pricing calculator may not have been completed or submitted for this request.
            </Box>
          </StatusIndicator>
        </Box>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      {/* EKS / ECS / Move-to-Containers disclaimer */}
      {services.some(s => /eks|ecs/i.test(s.service_code || '')) && (
        <Alert type="warning" header="Container Workloads - Pricing Estimation Note" statusIconAriaLabel="Warning">
          The AWS Pricing Calculator includes Elastic Kubernetes Service (EKS) and Elastic Compute Cloud (EC2) estimations. Chronos cannot automatically associate EC2 instances with their corresponding EKS clusters. This may result in an inaccurate modernization index, as EC2 costs are not included in the modern services ARR.
        </Alert>
      )}
      {/* Pricing Calculator Link */}
      {(() => {
        const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
        const calculatorUrl = fundRequest.artifacts?.pricingCalculatorLinks?.[0] || 
                             pricingData.calculator_url;
        
        if (calculatorUrl) {
          return (
            <Container>
              <Box textAlign="center" padding="m">
                <Button
                  variant="primary"
                  iconName="external"
                  onClick={() => window.open(calculatorUrl, '_blank')}
                >
                  Open AWS Pricing Calculator
                </Button>
              </Box>
            </Container>
          );
        }
        return null;
      })()}

      {/* Enhanced Dashboard Summary - Always visible */}
      {renderEnhancedSummary()}

      {/* Modernization Index */}
      {(() => {
        const modernizationData = calculatorDetails?.modernizationPathways || {};
        const modernizationIndex = modernizationData.modernizationIndex || 0;
        const totalModernArr = modernizationData.modernARR || 0;
        const totalAnnualCost = modernizationData.totalARR || 0;
        
        // Use backend pathway data
        const pathwayGroups = {};
        if (modernizationData.pathways) {
          modernizationData.pathways.forEach(pathway => {
            pathwayGroups[pathway.name] = {
              count: pathway.serviceCount,
              arr: pathway.arr
            };
          });
        }

        return (
          <Container header={<Header variant="h2">Modernization Index</Header>}>
            {loading ? (
              <Box textAlign="center" padding="l">
                <Spinner size="normal" />
                <Box variant="p" color="text-body-secondary">Loading modernization analysis...</Box>
              </Box>
            ) : calculatorDetails && services.length > 0 ? (
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Overall Modernization Score</Box>
                  <Box variant="h2" color={modernizationIndex > 50 ? "text-status-success" : modernizationIndex > 25 ? "text-status-warning" : "text-status-error"}>
                    {modernizationIndex}%
                  </Box>
                  <ProgressBar 
                    value={modernizationIndex} 
                    variant={modernizationIndex > 50 ? "success" : "warning"}
                    additionalInfo={`${formatCurrency(totalModernArr)} of ${formatCurrency(totalAnnualCost)} ARR is modern`}
                  />
                </div>
                <div>
                  <Box variant="awsui-key-label">Total Modern ARR</Box>
                  <Box variant="h3" color="text-status-success">
                    {formatCurrency(totalModernArr)}
                  </Box>
                  <Box variant="small">ARR from modern AWS services</Box>
                </div>
              </ColumnLayout>
            ) : (
              <Alert type="info" header="Modernization Analysis Not Available">
                Modernization pathways analysis is not available for this fund request. This may indicate that the pricing calculator has not been processed yet or no calculator URL was provided.
              </Alert>
            )}
          </Container>
        );
      })()}

      {/* Main Dashboard Tabs */}
      <Tabs
        tabs={mainTabs}
        activeTabId={activeMainTab}
        onChange={({ detail }) => setActiveMainTab(detail.activeTabId)}
        variant="container"
      />
    </SpaceBetween>
  );
};

export default PricingCalculatorTab;